import { Button, ButtonProps, Dialog, IconButton, styled } from "@mui/material";
import { FlagIcon, SellBagIcon, SmCircleChatIcon } from "assets/icons";
import { ReportForm, StyledATag } from "components";
import { PATHS } from "constants/routes";
import useChat from "hooks/useChat";
import useUser from "hooks/useUser";
import { MouseEvent, useState } from "react";
import { useIntl } from "react-intl";
import { StyledChatIcon, StyledWhiteButton } from "./Button.styles";

const ConfirmButton = (props: ButtonProps) => {
  return <StyledConfirmButton {...props} />;
};

const AddButton = (props: ButtonProps) => {
  return <StyledAddButton {...props} />;
};

const CancelButton = (props: ButtonProps) => {
  return <StyledCancelButton {...props} />;
};

const RoundButton = (props: ButtonProps) => {
  return <StyledRoundButton {...props} />;
};

const AcceptButton = (props: ButtonProps) => {
  return <StyledAcceptButton {...props} />;
};

const YellowButton = (props: ButtonProps) => {
  return <StyledConfirmButton {...props} />;
};

const BaseButton = (props: ButtonProps) => {
  return <StyledBaseButton {...props} />;
};

const TextButton = (props: ButtonProps) => {
  return <StyledTextButton variant="text" {...props} />;
};

const DeleteButton = (props: ButtonProps) => {
  return <StyledDeleteButton {...props} />;
};

export const GreenButton = (props: ButtonProps) => {
  return <StyledAcceptButton {...props} />;
};

export const WhiteButton = (props: ButtonProps) => {
  return <StyledWhiteButton {...props} />;
};

export const LgAcceptButton = (props: ButtonProps) => {
  return <StyledLgAcceptButton {...props} />;
};

export const SendMessageButton = ({
  identity,
  hideIcon,
  shorter,
}: {
  identity?: string;
  hideIcon?: boolean;
  shorter?: boolean;
}) => {
  const { onOpenConversationWithIdentity } = useChat();
  const t = useIntl();

  const onClickButton = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (identity) onOpenConversationWithIdentity(identity);
  };

  if (shorter)
    return (
      <StyledChatIcon onClick={onClickButton}>
        <SmCircleChatIcon />
      </StyledChatIcon>
    );

  return (
    <WhiteButton
      onClick={onClickButton}
      endIcon={hideIcon ? undefined : <SmCircleChatIcon />}
      className="send-msg-btn"
    >
      {t.formatMessage({ id: "send_message" })}
    </WhiteButton>
  );
};

export const ReportButton = ({
  mobile,
  type,
  title,
  reference_name,
}: {
  mobile?: boolean;
  type: string;
  title?: string;
  reference_name?: string;
}) => {
  const t = useIntl();
  const [showReport, setShowReport] = useState(false);

  const onCloseReport = () => setShowReport(false);

  return (
    <>
      {!mobile && (
        <IconButton className="fit" onClick={() => setShowReport(true)}>
          <FlagIcon />
        </IconButton>
      )}
      {mobile && (
        <StyledReportButton
          endIcon={<FlagIcon />}
          onClick={() => setShowReport(true)}
        >
          {t.formatMessage({ id: "report" })}
        </StyledReportButton>
      )}

      <Dialog open={showReport} onClose={onCloseReport}>
        <ReportForm
          onClose={onCloseReport}
          {...{ mobile, type, title, reference_name }}
        />
      </Dialog>
    </>
  );
};

export const SellingButton = (props: ButtonProps) => {
  const t = useIntl();
  const { isLoggedIn } = useUser();
  return (
    <StyledATag href={PATHS.bizDomain}>
      <StyledTextButton
        variant="text"
        style={{ color: "var(--green-600)" }}
        startIcon={<SellBagIcon />}
        {...props}
      >
        {t.formatMessage({ id: isLoggedIn ? "seller_center" : "StartSelling" })}
      </StyledTextButton>
    </StyledATag>
  );
};

const ButtonBase = styled(Button)({
  textTransform: "initial",
  borderRadius: 8,
  fontWeight: "bold",
  padding: "8px 16px",
  fontSize: 14,
});

const StyledConfirmButton = styled(Button)({
  borderRadius: 8,
  width: "100%",
  fontWeight: 600,
  textTransform: "initial",
  padding: "8px 16px",
  color: "#fff",
  "&": {
    background: "var(--color)",
  },
  ":hover": {
    background: "var(--yellow-600)",
  },
  "&.Mui-disabled": {
    // color: "rgba(0, 0, 0, 0.26)",
    color: "#fff",
    backgroundColor: "#c3c3c3",
  },
});

const StyledAddButton = styled(BaseButton)({
  color: "#fff",
  "&,:hover": {
    backgroundColor: "var(--sub-color)",
  },
  span: {
    marginRight: 4,
  },
});

const StyledDeleteButton = styled(BaseButton)({
  border: "none",
  color: "#fff",
  "&,:hover": {
    backgroundColor: "#D92D20",
  },
});

const StyledCancelButton = styled(ButtonBase)({
  color: "#000",
  border: "1px solid #D0D5DD",
  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
});

const StyledRoundButton = styled(ButtonBase)({
  color: "#000",
  border: "1px solid #D0D5DD",
  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
});

const StyledAcceptButton = styled(ButtonBase)({
  fontWeight: 500,
  color: "#fff",
  "&": {
    backgroundColor: "var(--sub-color)",
  },
  "&:hover": {
    backgroundColor: "var(--green-800)",
  },
  "&.Mui-disabled": {
    backgroundColor: "#D0D5DD",
    color: "#fff",
  },
});

const StyledBaseButton = styled(ButtonBase)({
  color: "#000",
  border: "1px solid #D0D5DD",
  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
  fontWeight: 500,
});

const StyledLgAcceptButton = styled(StyledAcceptButton)({
  fontWeight: 600,
  padding: "10px 18px",
});

const StyledTextButton = styled(Button)({
  textTransform: "initial",
  padding: 0,
  fontWeight: 600,
  fontSize: 16,
  ".MuiButton-icon": {
    marginLeft: 0,
  },
});

const StyledReportButton = styled(BaseButton)({
  svg: {
    width: 20,
    height: 20,
  },
  color: "var(--gray)",
  width: "fit-content",
  lineHeight: "20px",
});

export {
  ConfirmButton,
  AddButton,
  CancelButton,
  AcceptButton,
  BaseButton,
  RoundButton,
  TextButton,
  DeleteButton,
  YellowButton,
};
